import React, { useEffect, useRef, useState } from "react";
import "./AgencyCreateOnBoardingPost.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../../enums/common.enums";
import toast from "react-hot-toast";
import { Modal } from "@mui/material";
import { CommonHelpers } from "../../../../utils/helpers/common.helpers";
import PreviewModal from "../propertypost/view/PreviewModal";
import UpdateTemplateModal from "../../WorkspaceSheduleConfigure/UpdateTemplateModal";
import Loading from "../../../loading/Loading";
import OnBoardingConfigurePost from "./OnBoardingConfigurePost";
import Upload from "../../../../components/icons/Upload";
import OnBoardingPostTabs from "./OnBoardingPostTabs";
import OnBoardingPostType from "./OnBoardingPostType";
import { TemplatesApi } from "../../../../utils/api/Templates";
import { PropertyApi } from "../../../../utils/api/Property";
import clsx from "clsx";
import imageCompression from "browser-image-compression";
import { clientRoutes } from "../../../../utils/routes";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const AgencyCreateOnBoardingPost = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const step = searchParams.get("step") || "post_type";
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPostType, setSelectedPostType] = useState<string | null>(null);
  const [selectedPostTypeTitle, setSelectedPostTypeTitle] =
    useState<string>("N/A");
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templateData, setTemplateData] = useState<any>(null);
  const [inputs, setInputs] = useState<{ [key: string]: any }>({});
  const [onboardingPost, setOnboardingPost] = useState<any>(null);
  const onboardingPostId = searchParams.get("onboarding_post_id") ?? "";
  const [isGenerating, setIsGenerating] = useState(false);

  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [inputOrder, setInputOrder] = useState<string[]>([]);
  const [imageEditing, setImageEditing] = useState<{ [key: string]: boolean }>(
    {}
  );
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [draggedOverKey, setDraggedOverKey] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState<{
    [key: string]: boolean;
  }>({});

  const subscriptionType = searchParams.get("subscription_type") || "";
  const listingType = searchParams.get("listing_type") || "sale";
  const postTypeFromUrl =
    searchParams.get("post_type") || onboardingPost?.post_type || "";

  const handleFileUpdate = (file: File | undefined, key: string) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select or drop a valid image file.");
      setDraggedOverKey(null);
      return;
    }

    setIsImageLoading((prev) => ({ ...prev, [key]: true }));
    const url = URL.createObjectURL(file);

    setFormValues((prev) => ({
      ...prev,
      [key]: { ...prev[key], value: url, file, original_path: "" },
    }));

    setImageEditing((prev) => ({ ...prev, [key]: false }));
    setDraggedOverKey(null);

    setTimeout(() => {
      setIsImageLoading((prev) => ({ ...prev, [key]: false }));
    }, 500);
  };

  function resolveOnboardingTemplateType(
    postType?: string,
    listingType: string = "sale"
  ) {
    const normalizedPostType = (postType || "").toLowerCase();
    const normalizedListing = (listingType || "sale").toLowerCase();
    const prefix = `onboarding_for_${normalizedListing}`;

    if (normalizedPostType === "image_post") return `${prefix}_image_post`;
    if (normalizedPostType === "story_post") return `${prefix}_story_post`;
    return undefined;
  }

  const getSelectedTemplateId = (
    postType: string | undefined,
    onboardingPost?: any
  ) => {
    const normalizedPostType = (postType || "").toLowerCase();
    switch (normalizedPostType) {
      case "image_post":
        return (
          onboardingPost?.image_post_design_activity_ref?.design_template_id
            ?._id ||
          onboardingPost?.design_template_id?._id ||
          null
        );
      case "story_post":
        return (
          onboardingPost?.story_post_design_activity_ref?.design_template_id
            ?._id ||
          onboardingPost?.design_template_id?._id ||
          null
        );
      default:
        return onboardingPost?.design_template_id?._id || null;
    }
  };

  const fetchOnboardingTemplateId = async (
    subscriptionType: string,
    postType: string
  ) => {
    try {
      const response = await TemplatesApi.getTemplateConfigs({
        subscription_type: subscriptionType,
        listing_type: listingType,
      });
      if (!response?.success || !response.data?.data) {
        toast.error("Failed to load template configs!");
        return null;
      }
      const postTypeBlock = response.data.data[postType];
      if (!postTypeBlock || !postTypeBlock.templates) {
        toast.error(`No templates configured for ${postType}.`);
        return null;
      }
      const expectedSlug = resolveOnboardingTemplateType(postType);
      if (!expectedSlug) {
        console.warn(`Unsupported post type for onboarding: ${postType}`);
        return null;
      }
      const foundTemplate = postTypeBlock.templates.find(
        (tpl: any) =>
          tpl.slug === expectedSlug && tpl.config?.design_template?._id
      );
      return foundTemplate?.config.design_template._id || null;
    } catch (err) {
      console.error(err);
      toast.error("Error fetching template configs!");
      return null;
    }
  };

  const fetchTemplate = async (providedDesignTemplateId?: string) => {
    setIsLoading(true);
    try {
      let existingGeneratedImage: string | null = null;
      let onboardingPostData: any = null;
      let designTemplateId: string | null = providedDesignTemplateId || null;

      if (onboardingPostId) {
        const onboardingRes = await PropertyApi.GetOnboardingPostById(
          onboardingPostId
        );
        onboardingPostData = onboardingRes?.data?.data;

        if (onboardingPostData) {
          setOnboardingPost(onboardingPostData);

          const refs = [
            onboardingPostData?.image_post_design_activity_ref,
            onboardingPostData?.story_post_design_activity_ref,
          ].filter(Boolean);

          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );

          if (firstWithImage) {
            existingGeneratedImage = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
          }

          designTemplateId = getSelectedTemplateId(
            selectedPostType || undefined,
            onboardingPostData
          );

          if (!existingGeneratedImage && refs.length > 0) {
            setIsGenerating(true);

            const pollInterval = 3000;

            const pollDesignStatus = async () => {
              try {
                const check = await PropertyApi.GetOnboardingPostById(
                  onboardingPostId
                );

                const updatedData = check?.data?.data;

                const updatedRefs = [
                  updatedData?.image_post_design_activity_ref,
                  updatedData?.story_post_design_activity_ref,
                ].filter(Boolean);

                const firstUpdatedWithImage = updatedRefs.find(
                  (ref) => ref?.design_task_id?.output_image_path
                );

                if (firstUpdatedWithImage) {
                  const assetUrl = CommonHelpers.getImageUrlFromImageTool({
                    path: firstUpdatedWithImage.design_task_id
                      .output_image_path,
                  });

                  setTemplateData((prev: any) => ({
                    ...prev,
                    generatedImage: assetUrl,
                  }));

                  setIsGenerating(false);
                  toast.success("Design ready!");
                } else {
                  setTimeout(pollDesignStatus, pollInterval);
                }
              } catch (err) {
                console.error("Polling error:", err);
                setIsGenerating(false);
                toast.error("Error checking design status.");
              }
            };

            pollDesignStatus();
          }
        }
      }

      if (!designTemplateId) {
        designTemplateId = await fetchOnboardingTemplateId(
          subscriptionType,
          selectedPostType || "image_post"
        );
      }

      if (!designTemplateId) {
        toast.error("No matching onboarding template found!");
        setIsLoading(false);
        return;
      }

      const res = await TemplatesApi.getDesignTemplateById(designTemplateId);

      if (res.success && res.data) {
        const templateDetails = res.data.data;
        setTemplateData({ data: templateDetails });

        const templateFallbackImage =
          templateDetails.template_image_ref?.original_file_path ||
          templateDetails.template_image;

        setTemplateData((prev: any) => ({
          ...prev,
          generatedImage:
            existingGeneratedImage ||
            (templateFallbackImage
              ? `${API_BASE_URL}/${templateFallbackImage}`
              : null),
        }));

        const apiInputs = templateDetails.inputs || {};
        setInputs(apiInputs);
        setInputOrder(Object.keys(apiInputs));

        const existingInputs = onboardingPostData?.inputs || {};
        const newFormValues: { [key: string]: any } = {};

        Object.keys(apiInputs).forEach((key, index) => {
          const apiInput = apiInputs[key];
          const existingValue = existingInputs[key];
          const isImage = apiInput.input_type === "image";

          let value: any = "";
          let original_path: string | null = null;

          if (isImage) {
            original_path =
              existingValue?.filepath ?? apiInput.filepath ?? null;
            if (original_path) {
              value = CommonHelpers.getImageUrlFromImageTool({
                path: original_path,
              });
            }
          } else {
            value = existingValue?.value ?? apiInput.value ?? "";
          }

          newFormValues[key] = {
            value,
            original_path,
            file: null,
            handler: apiInput.handler || key,
            label: apiInput.label || key,
            template_input_index: apiInput.template_input_index ?? index,
            characters_limit: apiInput.characters_limit || 100,
            input_type: apiInput.input_type || "text",
            enabled: existingValue?.enabled ?? apiInput.enabled ?? true,
            use_main_image: isImage ? apiInput.use_main_image ?? false : false,
          };
        });

        setFormValues(newFormValues);
      } else {
        toast.error(res.error || "Failed to fetch template!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch template data!");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (step === CommonEnums.onBoardingPostTabs.create_post.key) {
      fetchTemplate();
    }
  }, [step, subscriptionType, selectedPostType]);

  const buildInputsPayload = async () => {
    const imageKeysWithFiles = Object.keys(formValues).filter(
      (key) => formValues[key]?.input_type === "image" && formValues[key]?.file
    );

    const uploadedFilePaths: Record<string, string> = {};

    if (imageKeysWithFiles.length > 0) {
      const uploadEndpoint = `${process.env.REACT_APP_DESIGN_TOOL_ENDPOINT}/api/uploads/upload-template-input-images`;
      const formData = new FormData();

      for (const key of imageKeysWithFiles) {
        const fileObj = formValues[key].file;
        if (!fileObj) continue;
        try {
          const compressedFile = await imageCompression(fileObj, {
            useWebWorker: true,
            fileType: fileObj.type,
          });
          formData.append("input_image", compressedFile, fileObj.name);
        } catch (error) {
          toast.error(`Image compression failed for: ${formValues[key].label}`);
          return null;
        }
      }

      try {
        const response = await fetch(uploadEndpoint, {
          method: "POST",
          body: formData,
        });
        const data = await response.json();

        if (data?.success && Array.isArray(data.data)) {
          data.data.forEach((filePathData: any, index: number) => {
            const key = imageKeysWithFiles[index];
            const path = filePathData?.filepath || filePathData?.path;
            if (path) uploadedFilePaths[key] = path;
          });
        } else {
          throw new Error("Bulk image upload response was not successful.");
        }
      } catch (uploadErr) {
        toast.error("Failed to upload image(s). Please try again.");
        return null;
      }
    }

    const inputsPayload: Record<string, any> = {};
    Object.keys(formValues).forEach((key) => {
      const val = formValues[key];
      if (!val || !inputs[key]) return;

      if (val.input_type === "image") {
        inputsPayload[key] = {
          ...inputs[key],
          filepath: uploadedFilePaths[key] || val.original_path || "",
          enabled: val.enabled ?? true,
          use_main_image: !val.file && (val.use_main_image ?? false),
        };
      } else {
        inputsPayload[key] = {
          ...inputs[key],
          value: val.value,
          enabled: val.enabled ?? true,
        };
      }
    });
    return inputsPayload;
  };

  const handleApiSubmit = async () => {
    if (!onboardingPostId) return { success: false };
    const inputsPayload = await buildInputsPayload();
    if (!inputsPayload) return { success: false };

    const payload = { inputs: inputsPayload, finalize: true };
    const res = await PropertyApi.updateOnboardingPost(
      payload,
      onboardingPostId
    );

    if (!res?.data?.success) {
      toast.error(res?.data?.message || "Failed to update post.");
      return { success: false };
    }
    return { success: true };
  };

  const handleConfirmOnboardingPost = async () => {
    setProcessingModalOpen(true);
    const { success } = await handleApiSubmit();
    if (success) {
      toast.success("Onboarding post confirmed successfully!");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", CommonEnums.onBoardingPostTabs.configure_post.key);
        return next;
      });
    }
    setProcessingModalOpen(false);
  };

  const handleUpdateAndGenerateDesign = async () => {
    setProcessingModalOpen(true);
    const { success } = await handleApiSubmit();
    setProcessingModalOpen(false);

    if (success) {
      setIsGenerating(true);
      const pollInterval = 3000;
      const pollDesignStatus = async () => {
        try {
          const check = await PropertyApi.GetOnboardingPostById(
            onboardingPostId
          );
          const postData = check?.data?.data;
          const refs = [
            postData?.image_post_design_activity_ref,
            postData?.story_post_design_activity_ref,
          ].filter(Boolean);
          const firstWithImage = refs.find(
            (ref) => ref?.design_task_id?.output_image_path
          );
          if (firstWithImage) {
            const assetUrl = CommonHelpers.getImageUrlFromImageTool({
              path: firstWithImage.design_task_id.output_image_path,
            });
            setTemplateData((prev: any) => ({
              ...prev,
              generatedImage: assetUrl,
            }));
            setIsGenerating(false);
            toast.success("Design generated successfully!");
          } else {
            setTimeout(pollDesignStatus, pollInterval);
          }
        } catch (err) {
          console.error("Polling error:", err);
          setIsGenerating(false);
        }
      };
      pollDesignStatus();
    }
  };

  const resetInputs = () => window.location.reload();
  const getPreviewImage = () =>
    templateData?.generatedImage ||
    (templateData?.data?.template_image
      ? `${API_BASE_URL}/${templateData.data.template_image}`
      : "/images/image-video-generation-error-fallback-image-for-card.png");
  const getPreviewContainerClass = (postType: string | null) =>
    postType === CommonEnums.postType.story_post.key
      ? "story-post-preview"
      : "image-post-preview";

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="template-inputs-wrapper">
          <div className="top-header-bar">
            <div
              className="agent-view-back-btn"
              onClick={() =>
                navigate(
                  `${clientRoutes.AgencyOnboardingPostList}?subscription_type=${subscriptionType}`
                )
              }
            >
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <p className="bakc-text-contain">Back to List</p>
            </div>
            <div className="page-title">Create Onboarding Post</div>
          </div>
          <OnBoardingPostTabs
            activeTab={step}
            onTabChange={(newStep) =>
              setSearchParams((prev) => {
                const n = new URLSearchParams(prev);
                n.set("step", newStep);
                return n;
              })
            }
          />
          {step === CommonEnums.onBoardingPostTabs.create_post.key && (
            <>
              {isLoading ? (
                <div className="list-page-loading">
                  <Loading />
                </div>
              ) : (
                <div className="template-inputs-grid">
                  <div className="template-input-fields">
                    <div className="section-heading">Inputs</div>
                    <div className="input-description">
                      Feel free to update the inputs to customize the post
                    </div>
                    {inputOrder
                      .filter((key) => formValues[key])
                      .map((key) => {
                        const item = formValues[key];
                        if (!item) return null;
                        const isImage = item.input_type === "image";
                        const value = item.value || "";
                        const charLimit = item.characters_limit || 100;
                        const isEditing = imageEditing[key] || false;
                        const isEnabled = item.enabled ?? true;

                        if (isImage) {
                          return (
                            <div className="input-field-block" key={key}>
                              <div className="image-block">
                                <div className="employee-image-header">
                                  <label className="employee-input-label">
                                    {item.label}
                                  </label>
                                  <button
                                    className="change-image-btn"
                                    type="button"
                                    onClick={() =>
                                      setImageEditing((p) => ({
                                        ...p,
                                        [key]: !isEditing,
                                      }))
                                    }
                                  >
                                    {isEditing ? "Cancel" : "Change Image"}
                                  </button>
                                </div>
                                {!isEditing && (
                                  <>
                                    {isImageLoading[key] ? (
                                      <div className="employee-image-loading-preview">
                                        <div className="spinner"></div>
                                      </div>
                                    ) : (
                                      value && (
                                        <div className="employee-image-preview">
                                          <img
                                            src={value}
                                            alt={item.label}
                                            className="employee-image"
                                            onError={(e) => {
                                              e.currentTarget.src =
                                                "/images/image-video-generation-error-fallback-image-for-card.png";
                                            }}
                                          />
                                        </div>
                                      )
                                    )}
                                  </>
                                )}
                                {isEditing && (
                                  <div
                                    className={clsx("employee-image-upload", {
                                      "drag-over": draggedOverKey === key,
                                    })}
                                    onClick={() =>
                                      fileInputRefs.current[key]?.click()
                                    }
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      handleFileUpdate(
                                        e.dataTransfer.files?.[0],
                                        key
                                      );
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDragEnter={() => setDraggedOverKey(key)}
                                    onDragLeave={() => setDraggedOverKey(null)}
                                  >
                                    <input
                                      type="file"
                                      accept="image/*"
                                      ref={(el) =>
                                        (fileInputRefs.current[key] = el)
                                      }
                                      style={{ display: "none" }}
                                      onChange={(e) =>
                                        handleFileUpdate(
                                          e.target.files?.[0],
                                          key
                                        )
                                      }
                                    />
                                    <div className="upload-icon">
                                      <Upload />
                                    </div>
                                    <p>Drop or Select file</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="input-field-block" key={key}>
                            <div className="input-label-row">
                              <label className="input-label">
                                {item.label} ({value.length}/{charLimit})
                              </label>
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={(e) =>
                                    setFormValues((prev) => ({
                                      ...prev,
                                      [key]: {
                                        ...prev[key],
                                        enabled: e.target.checked,
                                      },
                                    }))
                                  }
                                />
                                <span className="slider round"></span>
                              </label>
                            </div>
                            <textarea
                              className="input-textarea"
                              placeholder={item.label}
                              maxLength={charLimit}
                              disabled={!isEnabled}
                              value={value}
                              onChange={(e) =>
                                setFormValues((prev) => ({
                                  ...prev,
                                  [key]: {
                                    ...prev[key],
                                    value: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        );
                      })}
                    <div className="action-text-buttons">
                      <span
                        className={clsx("text-button", {
                          disabled: isGenerating,
                        })}
                        onClick={() => !isGenerating && resetInputs()}
                      >
                        Reset Inputs
                      </span>
                      <span
                        className={clsx("text-button", {
                          disabled: isGenerating,
                        })}
                        onClick={() =>
                          !isGenerating && handleUpdateAndGenerateDesign()
                        }
                      >
                        Update & Generate New Design
                      </span>
                    </div>
                  </div>
                  <div className="template-preview">
                    <div className="section-heading">Post Preview</div>
                    <div className="template-preview-label">
                      <span>Preview of design template</span>
                      <div className="preview-actions">
                        <span
                          className={clsx("preview-link", {
                            disabled: isGenerating,
                          })}
                          onClick={() => !isGenerating && setPreviewOpen(true)}
                        >
                          Preview
                        </span>
                        <span
                          className={clsx("preview-link", {
                            disabled: isGenerating,
                          })}
                          onClick={() =>
                            !isGenerating && setIsUpdateTemplateOpen(true)
                          }
                        >
                          Change Template
                        </span>
                      </div>
                    </div>
                    <div
                      className={`activity-image-section ${getPreviewContainerClass(
                        postTypeFromUrl
                      )} ${isGenerating ? "loading" : ""}`}
                    >
                      {isGenerating ? (
                        <div className="post-preview-loading">
                          <div className="spinner" />
                          <h4>Your request is being processed.</h4>
                          <p>
                            We're working on your request! Creating a
                            high-quality output can take a little while. We
                            appreciate your patience.
                          </p>
                          <p className="message">
                            Feel free to wait here, or browse other pages. We'll
                            send you a notification when your request is
                            completed.
                          </p>
                        </div>
                      ) : (
                        <img
                          src={getPreviewImage()}
                          alt="Template Preview"
                          className="activity-thumbnail"
                          onError={(e) => {
                            e.currentTarget.src =
                              "/images/image-video-generation-error-fallback-image-for-card.png";
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!isLoading && (
                <>
                  <div className="footer-divider" />
                  <div className="footer-buttons">
                    <button
                      className="btn btn-secondary"
                      onClick={() => navigate(-1)}
                      disabled={isGenerating}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleConfirmOnboardingPost}
                      disabled={isGenerating}
                    >
                      Confirm
                    </button>
                  </div>
                </>
              )}
            </>
          )}
          <PreviewModal
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            images={[getPreviewImage()]}
          />
          {step === CommonEnums.onBoardingPostTabs.configure_post.key && (
            <OnBoardingConfigurePost
              selectedPostTypeTitle={selectedPostTypeTitle}
            />
          )}
          {step === CommonEnums.onBoardingPostTabs.post_type.key && (
            <OnBoardingPostType
              selectedType={selectedPostType}
              onSelectType={setSelectedPostType}
              onContinue={() =>
                setSearchParams((prev) => {
                  const n = new URLSearchParams(prev);
                  n.set("step", CommonEnums.onBoardingPostTabs.create_post.key);
                  return n;
                })
              }
            />
          )}

          {isUpdateTemplateOpen && (
            <UpdateTemplateModal
              open={isUpdateTemplateOpen}
              onClose={() => setIsUpdateTemplateOpen(false)}
              selectedDesignTemplateId={getSelectedTemplateId(
                postTypeFromUrl ?? undefined,
                onboardingPost
              )}
              templateTypeId={resolveOnboardingTemplateType(
                postTypeFromUrl || onboardingPost?.post_type,
                listingType
              )}
              subscriptionType="agent_marketing"
              postType={postTypeFromUrl ?? undefined}
              listingType={listingType}
              onConfirm={async () => {
                if (!onboardingPostId) {
                  toast.error("Missing onboarding post ID");
                  return;
                }

                try {
                  setProcessingModalOpen(true);

                  const designTemplateId = await fetchOnboardingTemplateId(
                    subscriptionType,
                    postTypeFromUrl
                  );

                  if (!designTemplateId) {
                    toast.error("No matching template found!");
                    setIsLoading(false);
                    return;
                  }

                  const payload: Record<string, any> = {
                    inputs: {},
                    image_post_design_activity_ref: null,
                    story_post_design_activity_ref: null,
                    finalize: "true",
                  };

                  const res = await PropertyApi.updateOnboardingPost(
                    payload,
                    onboardingPostId
                  );

                  if (!res?.data?.success) {
                    toast.error(
                      res?.data?.message || "Failed to update onboarding post"
                    );
                    return;
                  }

                  setIsUpdateTemplateOpen(false);
                  setProcessingModalOpen(false);

                  await fetchTemplate(designTemplateId);
                  setIsGenerating(true);

                  const pollInterval = 3000;

                  const pollStatus = async () => {
                    try {
                      const check = await PropertyApi.GetOnboardingPostById(
                        onboardingPostId
                      );
                      const postData = check?.data?.data;

                      const refs = [
                        postData?.image_post_design_activity_ref,
                        postData?.story_post_design_activity_ref,
                      ].filter(Boolean);

                      const firstWithImage = refs.find(
                        (ref) => ref?.design_task_id?.output_image_path
                      );

                      if (firstWithImage) {
                        const assetUrl = CommonHelpers.getImageUrlFromImageTool(
                          {
                            path: firstWithImage.design_task_id
                              .output_image_path,
                          }
                        );

                        setTemplateData((prev: any) => ({
                          ...prev,
                          generatedImage: assetUrl,
                        }));

                        setIsGenerating(false);
                      } else {
                        setTimeout(pollStatus, pollInterval);
                      }
                    } catch (err) {
                      console.error("Polling error:", err);
                      setIsGenerating(false);
                    }
                  };

                  pollStatus();
                } catch (err) {
                  console.error(err);
                  toast.error(
                    "Something went wrong while updating onboarding post"
                  );
                }
              }}
            />
          )}
          <Modal
            open={processingModalOpen}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request...
                    <br />
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgencyCreateOnBoardingPost;
